import React, { useState, useEffect } from 'react';
import { 
  History, Eye, Calendar, User, Shield, RefreshCw, 
  Filter, Download, Search, MapPin, Activity, Clock
} from 'lucide-react';
import toast from 'react-hot-toast';

/**
 * Admin Audit Logs Page
 * Allows administrators to view all moderator actions across schools with filtering
 */
const AuditLogsPage = () => {
  const [auditLogs, setAuditLogs] = useState([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  const [filters, setFilters] = useState({
    schoolId: '',
    moderatorId: '',
    action: '',
    resourceType: '',
    fromDate: '',
    toDate: ''
  });
  const [pagination, setPagination] = useState({
    page: 1,
    limit: 50,
    total: 0,
  });
  const [schools, setSchools] = useState([]);
  const [moderators, setModerators] = useState([]);
  const [expandedLog, setExpandedLog] = useState(null);

  // Load audit logs from API
  useEffect(() => {
    const fetchAuditLogs = async () => {
      try {
        setLoading(true);
        
        // Build query parameters for filtering and pagination
        const params = new URLSearchParams({
          limit: pagination.limit,
          offset: (pagination.page - 1) * pagination.limit,
          ...(filters.schoolId && { schoolId: filters.schoolId }),
          ...(filters.moderatorId && { moderatorId: filters.moderatorId }),
          ...(filters.action && { action: filters.action }),
          ...(filters.resourceType && { resourceType: filters.resourceType }),
          ...(filters.fromDate && { fromDate: filters.fromDate }),
          ...(filters.toDate && { toDate: filters.toDate })
        });

        const response = await fetch(`${process.env.REACT_APP_API_URL}/api/admin/audit-logs?${params}`, {
          headers: {
            'Authorization': `Bearer ${localStorage.getItem('token')}`
          }
        });

        if (!response.ok) {
          throw new Error('Failed to fetch audit logs');
        }

        const data = await response.json();
        setAuditLogs(data.auditLogs || []);
        setPagination(prev => ({
          ...prev,
          total: data.total || 0
        }));
        setError(null);
      } catch (err) {
        console.error('Error fetching audit logs:', err);
        setError(err.message);
        // Only show toast for actual errors, not empty results
        if (err.message !== 'Failed to fetch audit logs') {
          toast.error('Failed to load audit logs: ' + err.message);
        }
      } finally {
        setLoading(false);
      }
    };

    fetchAuditLogs();
  }, [pagination.page, pagination.limit, filters]);

  // Load schools for filtering
  useEffect(() => {
    const fetchSchools = async () => {
      try {
        const response = await fetch(`${process.env.REACT_APP_API_URL}/api/schools`, {
          headers: {
            'Authorization': `Bearer ${localStorage.getItem('token')}`
          }
        });

        if (response.ok) {
          const data = await response.json();
          // Handle both response formats: data.schools or data directly as array
          setSchools(data.schools || data || []);
        }
      } catch (err) {
        console.error('Error fetching schools:', err);
      }
    };

    fetchSchools();
  }, []);

  // Load moderators for filtering
  useEffect(() => {
    const fetchModerators = async () => {
      try {
        const response = await fetch(`${process.env.REACT_APP_API_URL}/api/admin/users?limit=1000`, {
          headers: {
            'Authorization': `Bearer ${localStorage.getItem('token')}`
          }
        });

        if (response.ok) {
          const data = await response.json();
          // Filter for moderators and admins only on the frontend
          const moderatorsAndAdmins = (data.users || []).filter(user => 
            user.role === 'moderator' || user.role === 'admin'
          );
          setModerators(moderatorsAndAdmins);
        }
      } catch (err) {
        console.error('Error fetching moderators:', err);
      }
    };

    fetchModerators();
  }, []);

  const handleFilterChange = (key, value) => {
    setFilters(prev => ({
      ...prev,
      [key]: value
    }));
    setPagination(prev => ({ ...prev, page: 1 }));
  };

  const handlePageChange = (newPage) => {
    setPagination(prev => ({ ...prev, page: newPage }));
  };

  const clearFilters = () => {
    setFilters({
      schoolId: '',
      moderatorId: '',
      action: '',
      resourceType: '',
      fromDate: '',
      toDate: ''
    });
    setPagination(prev => ({ ...prev, page: 1 }));
  };

  const formatDate = (timestamp) => {
    return new Date(timestamp).toLocaleString();
  };

  const getActionIcon = (action) => {
    if (action.includes('suspended') || action.includes('banned')) {
      return <Shield className="text-red-500" size={16} />;
    }
    if (action.includes('deleted') || action.includes('removed')) {
      return <Activity className="text-orange-500" size={16} />;
    }
    if (action.includes('approved') || action.includes('resolved')) {
      return <Activity className="text-green-500" size={16} />;
    }
    return <Activity className="text-blue-500" size={16} />;
  };

  const getActionColor = (action) => {
    if (action.includes('suspended') || action.includes('banned')) {
      return 'bg-red-100 text-red-800 dark:bg-red-900 dark:text-red-200';
    }
    if (action.includes('deleted') || action.includes('removed')) {
      return 'bg-orange-100 text-orange-800 dark:bg-orange-900 dark:text-orange-200';
    }
    if (action.includes('approved') || action.includes('resolved')) {
      return 'bg-green-100 text-green-800 dark:bg-green-900 dark:text-green-200';
    }
    return 'bg-blue-100 text-blue-800 dark:bg-blue-900 dark:text-blue-200';
  };

  const totalPages = Math.ceil(pagination.total / pagination.limit);

  if (loading) {
    return (
      <div className="flex items-center justify-center min-h-96">
        <RefreshCw className="animate-spin text-gray-500" size={32} />
        <span className="ml-2 text-gray-500">Loading audit logs...</span>
      </div>
    );
  }

  return (
    <div className="space-y-6">
      {/* Header */}
      <div className="flex items-center justify-between">
        <div>
          <h1 className="text-2xl font-bold text-gray-900 dark:text-white flex items-center">
            <History className="mr-2" size={28} />
            Audit Logs
          </h1>
          <p className="text-gray-600 dark:text-gray-400 mt-1">
            Track moderator actions across all schools
          </p>
        </div>
        <button
          onClick={() => window.location.reload()}
          className="flex items-center px-4 py-2 bg-red-600 text-white rounded-lg hover:bg-red-700 transition-colors"
        >
          <RefreshCw size={16} className="mr-2" />
          Refresh
        </button>
      </div>

      {/* Filters */}
      <div className="bg-white dark:bg-gray-800 rounded-lg shadow p-6">
        <div className="flex items-center mb-4">
          <Filter size={20} className="mr-2 text-gray-500" />
          <h3 className="text-lg font-semibold">Filters</h3>
          <button
            onClick={clearFilters}
            className="ml-auto text-sm text-red-600 hover:text-red-700"
          >
            Clear All
          </button>
        </div>
        
        <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-4">
          {/* School Filter */}
          <div>
            <label className="block text-sm font-medium mb-2">School</label>
            <select
              value={filters.schoolId}
              onChange={(e) => handleFilterChange('schoolId', e.target.value)}
              className="w-full p-2 border rounded-lg dark:bg-gray-700 dark:border-gray-600"
            >
              <option value="">All Schools</option>
              {schools.map(school => (
                <option key={school.id} value={school.id}>
                  {school.name}
                </option>
              ))}
            </select>
          </div>

          {/* Moderator Filter */}
          <div>
            <label className="block text-sm font-medium mb-2">Moderator</label>
            <select
              value={filters.moderatorId}
              onChange={(e) => handleFilterChange('moderatorId', e.target.value)}
              className="w-full p-2 border rounded-lg dark:bg-gray-700 dark:border-gray-600"
            >
              <option value="">All Moderators</option>
              {moderators.map(moderator => (
                <option key={moderator.id} value={moderator.id}>
                  {moderator.name} ({moderator.email})
                </option>
              ))}
            </select>
          </div>

          {/* Action Filter */}
          <div>
            <label className="block text-sm font-medium mb-2">Action</label>
            <select
              value={filters.action}
              onChange={(e) => handleFilterChange('action', e.target.value)}
              className="w-full p-2 border rounded-lg dark:bg-gray-700 dark:border-gray-600"
            >
              <option value="">All Actions</option>
              <option value="user_suspended">User Suspended</option>
              <option value="user_banned">User Banned</option>
              <option value="user_status_changed">User Status Changed</option>
              <option value="user_role_changed">User Role Changed</option>
              <option value="material_deleted">Material Deleted</option>
              <option value="report_resolved">Report Resolved</option>
              <option value="report_approved">Report Approved</option>
              <option value="report_rejected">Report Rejected</option>
            </select>
          </div>

          {/* Resource Type Filter */}
          <div>
            <label className="block text-sm font-medium mb-2">Resource Type</label>
            <select
              value={filters.resourceType}
              onChange={(e) => handleFilterChange('resourceType', e.target.value)}
              className="w-full p-2 border rounded-lg dark:bg-gray-700 dark:border-gray-600"
            >
              <option value="">All Types</option>
              <option value="user">User</option>
              <option value="material">Material</option>
              <option value="report">Report</option>
              <option value="comment">Comment</option>
              <option value="course">Course</option>
              <option value="professor">Professor</option>
            </select>
          </div>

          {/* From Date */}
          <div>
            <label className="block text-sm font-medium mb-2">From Date</label>
            <input
              type="date"
              value={filters.fromDate}
              onChange={(e) => handleFilterChange('fromDate', e.target.value)}
              className="w-full p-2 border rounded-lg dark:bg-gray-700 dark:border-gray-600"
            />
          </div>

          {/* To Date */}
          <div>
            <label className="block text-sm font-medium mb-2">To Date</label>
            <input
              type="date"
              value={filters.toDate}
              onChange={(e) => handleFilterChange('toDate', e.target.value)}
              className="w-full p-2 border rounded-lg dark:bg-gray-700 dark:border-gray-600"
            />
          </div>
        </div>
      </div>

      {/* Results Summary */}
      <div className="flex items-center justify-between text-sm text-gray-600 dark:text-gray-400">
        <span>
          Showing {Math.min((pagination.page - 1) * pagination.limit + 1, pagination.total)}-
          {Math.min(pagination.page * pagination.limit, pagination.total)} of {pagination.total} logs
        </span>
      </div>

      {/* Audit Logs Table */}
      <div className="bg-white dark:bg-gray-800 rounded-lg shadow overflow-hidden">
        {error ? (
          <div className="p-8 text-center">
            <div className="text-red-500 mb-2">Failed to load audit logs</div>
            <button
              onClick={() => window.location.reload()}
              className="text-red-600 hover:text-red-700"
            >
              Try again
            </button>
          </div>
        ) : auditLogs.length === 0 ? (
          <div className="p-8 text-center text-gray-500">
            <History size={48} className="mx-auto mb-4 opacity-50" />
            <p>No audit logs found with current filters</p>
          </div>
        ) : (
          <div className="overflow-x-auto">
            <table className="w-full">
              <thead className="bg-gray-50 dark:bg-gray-700">
                <tr>
                  <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 dark:text-gray-300 uppercase tracking-wider">
                    Action
                  </th>
                  <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 dark:text-gray-300 uppercase tracking-wider">
                    Moderator
                  </th>
                  <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 dark:text-gray-300 uppercase tracking-wider">
                    School
                  </th>
                  <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 dark:text-gray-300 uppercase tracking-wider">
                    Resource
                  </th>
                  <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 dark:text-gray-300 uppercase tracking-wider">
                    Date
                  </th>
                  <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 dark:text-gray-300 uppercase tracking-wider">
                    Actions
                  </th>
                </tr>
              </thead>
              <tbody className="bg-white dark:bg-gray-800 divide-y divide-gray-200 dark:divide-gray-700">
                {auditLogs.map((log) => (
                  <React.Fragment key={log.id}>
                    <tr className="hover:bg-gray-50 dark:hover:bg-gray-700">
                      <td className="px-6 py-4 whitespace-nowrap">
                        <div className="flex items-center">
                          {getActionIcon(log.action)}
                          <span className={`ml-2 px-2 py-1 text-xs rounded-full ${getActionColor(log.action)}`}>
                            {log.action.replace(/_/g, ' ').replace(/\b\w/g, l => l.toUpperCase())}
                          </span>
                        </div>
                      </td>
                      <td className="px-6 py-4 whitespace-nowrap">
                        <div className="flex items-center">
                          <User size={16} className="text-gray-400 mr-2" />
                          <div>
                            <div className="text-sm font-medium text-gray-900 dark:text-white">
                              {log.moderator.name}
                            </div>
                            <div className="text-sm text-gray-500 dark:text-gray-400">
                              {log.moderator.email}
                            </div>
                          </div>
                        </div>
                      </td>
                      <td className="px-6 py-4 whitespace-nowrap">
                        <div className="flex items-center">
                          <MapPin size={16} className="text-gray-400 mr-2" />
                          <span className="text-sm text-gray-900 dark:text-white">
                            {log.school.name}
                          </span>
                        </div>
                      </td>
                      <td className="px-6 py-4 whitespace-nowrap">
                        <div className="text-sm text-gray-900 dark:text-white">
                          {log.resourceType}
                          {log.resourceId && (
                            <div className="text-xs text-gray-500 dark:text-gray-400">
                              ID: {log.resourceId}
                            </div>
                          )}
                        </div>
                      </td>
                      <td className="px-6 py-4 whitespace-nowrap">
                        <div className="flex items-center">
                          <Clock size={16} className="text-gray-400 mr-2" />
                          <span className="text-sm text-gray-900 dark:text-white">
                            {formatDate(log.createdAt)}
                          </span>
                        </div>
                      </td>
                      <td className="px-6 py-4 whitespace-nowrap text-right text-sm font-medium">
                        <button
                          onClick={() => setExpandedLog(expandedLog === log.id ? null : log.id)}
                          className="text-red-600 hover:text-red-900 flex items-center"
                        >
                          <Eye size={16} className="mr-1" />
                          {expandedLog === log.id ? 'Hide' : 'View'} Details
                        </button>
                      </td>
                    </tr>
                    {expandedLog === log.id && (
                      <tr>
                        <td colSpan="6" className="px-6 py-4 bg-gray-50 dark:bg-gray-900">
                          <div className="space-y-3">
                            <h4 className="font-medium text-gray-900 dark:text-white">Action Details</h4>
                            
                            {/* IP and User Agent */}
                            <div className="grid grid-cols-1 md:grid-cols-2 gap-4 text-sm">
                              <div>
                                <span className="font-medium text-gray-700 dark:text-gray-300">IP Address:</span>
                                <span className="ml-2 text-gray-600 dark:text-gray-400">
                                  {log.ipAddress || 'N/A'}
                                </span>
                              </div>
                              <div>
                                <span className="font-medium text-gray-700 dark:text-gray-300">User Agent:</span>
                                <span className="ml-2 text-gray-600 dark:text-gray-400 break-all">
                                  {log.userAgent || 'N/A'}
                                </span>
                              </div>
                            </div>

                            {/* Action Details */}
                            {log.details && Object.keys(log.details).length > 0 && (
                              <div>
                                <span className="font-medium text-gray-700 dark:text-gray-300">Details:</span>
                                <pre className="mt-1 text-xs bg-gray-100 dark:bg-gray-800 p-2 rounded border overflow-x-auto">
                                  {JSON.stringify(log.details, null, 2)}
                                </pre>
                              </div>
                            )}
                          </div>
                        </td>
                      </tr>
                    )}
                  </React.Fragment>
                ))}
              </tbody>
            </table>
          </div>
        )}
      </div>

      {/* Pagination */}
      {totalPages > 1 && (
        <div className="flex items-center justify-between">
          <div className="text-sm text-gray-600 dark:text-gray-400">
            Page {pagination.page} of {totalPages}
          </div>
          <div className="flex space-x-2">
            <button
              onClick={() => handlePageChange(pagination.page - 1)}
              disabled={pagination.page === 1}
              className="px-3 py-1 bg-gray-200 dark:bg-gray-700 rounded disabled:opacity-50 disabled:cursor-not-allowed hover:bg-gray-300 dark:hover:bg-gray-600"
            >
              Previous
            </button>
            <button
              onClick={() => handlePageChange(pagination.page + 1)}
              disabled={pagination.page === totalPages}
              className="px-3 py-1 bg-gray-200 dark:bg-gray-700 rounded disabled:opacity-50 disabled:cursor-not-allowed hover:bg-gray-300 dark:hover:bg-gray-600"
            >
              Next
            </button>
          </div>
        </div>
      )}
    </div>
  );
};

export default AuditLogsPage;